import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { Field, FileField, SuccessPanel, useEnquiryForm, inputCls } from "../components/forms/fields";
import { FadeUp } from "../components/site/Reveal";

const INITIAL = {
    fullName: "",
    nationality: "",
    currentCountry: "",
    whatsapp: "",
    email: "",
    preferredLanguage: "",
    treatmentNeeded: "",
    preferredDestination: "",
    travelDates: "",
    lengthOfStay: "",
    medicalHistory: "",
    consentToShare: false,
};

export default function HealthcareEnquiry() {
    const [form, setForm] = useState(INITIAL);
    const [file, setFile] = useState(null);
    const { submitting, reference, submit } = useEnquiryForm("healthcare");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        const { consentToShare, ...data } = form;
        submit(data, file, consentToShare);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="healthcare-enquiry-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Healthcare Enquiry | International Patient Coordination — Viet Medica Ltd",
                        description:
                            "Tell us what medical support you need. Viet Medica Ltd coordinates international patient enquiries across Vietnam, India, Thailand and China.",
                        path: "/healthcare-enquiry",
                    }}
                    crumbs={[{ label: "Healthcare & Medical Tourism", href: "/healthcare-medical-tourism" }, { label: "Healthcare Enquiry" }]}
                    eyebrow="Healthcare & Medical Tourism"
                    title="Healthcare"
                    titleEm="Enquiry."
                    copy="Share the details of the support you need. Your enquiry goes directly to our healthcare coordination team — never to a public or trading channel."
                />

                <section className="bg-navy-deep py-16 lg:py-24">
                    <div className="mx-auto max-w-3xl px-5 lg:px-10">
                        {reference ? (
                            <SuccessPanel reference={reference} categoryLabel="healthcare" />
                        ) : (
                            <FadeUp>
                                <div className="mb-8 flex items-start gap-4 border border-amber-500/40 bg-amber-500/10 p-5" data-testid="emergency-warning">
                                    <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-400" aria-hidden="true" />
                                    <p className="text-sm leading-relaxed text-amber-200/90">
                                        Do not use this form for life-threatening emergencies. If you need urgent
                                        medical help, contact your local emergency services immediately.
                                    </p>
                                </div>
                                <form onSubmit={onSubmit} className="space-y-6 border border-white/10 bg-navy-surface p-7 lg:p-10" data-testid="healthcare-enquiry-form">
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <Field label="Full Name" htmlFor="hc-name" required>
                                            <input id="hc-name" data-testid="hc-name" required className={inputCls} value={form.fullName} onChange={set("fullName")} />
                                        </Field>
                                        <Field label="Nationality" htmlFor="hc-nationality" required>
                                            <input id="hc-nationality" data-testid="hc-nationality" required className={inputCls} value={form.nationality} onChange={set("nationality")} />
                                        </Field>
                                        <Field label="Current Country" htmlFor="hc-country" required>
                                            <input id="hc-country" data-testid="hc-country" required className={inputCls} value={form.currentCountry} onChange={set("currentCountry")} />
                                        </Field>
                                        <Field label="WhatsApp Number" htmlFor="hc-whatsapp" required>
                                            <input id="hc-whatsapp" data-testid="hc-whatsapp" required type="tel" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} />
                                        </Field>
                                        <Field label="Email" htmlFor="hc-email" required>
                                            <input id="hc-email" data-testid="hc-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} />
                                        </Field>
                                        <Field label="Preferred Language" htmlFor="hc-language">
                                            <input id="hc-language" data-testid="hc-language" className={inputCls} value={form.preferredLanguage} onChange={set("preferredLanguage")} />
                                        </Field>
                                        <Field label="Preferred Destination" htmlFor="hc-destination" required>
                                            <select id="hc-destination" data-testid="hc-destination" required className={inputCls} value={form.preferredDestination} onChange={set("preferredDestination")}>
                                                <option value="">Select…</option>
                                                <option>Vietnam</option>
                                                <option>India</option>
                                                <option>Thailand</option>
                                                <option>China</option>
                                                <option>Not sure yet</option>
                                            </select>
                                        </Field>
                                        <Field label="Preferred Travel Dates" htmlFor="hc-dates">
                                            <input id="hc-dates" data-testid="hc-dates" className={inputCls} value={form.travelDates} onChange={set("travelDates")} placeholder="e.g. March 2027" />
                                        </Field>
                                        <Field label="Approximate Length of Stay" htmlFor="hc-stay">
                                            <input id="hc-stay" data-testid="hc-stay" className={inputCls} value={form.lengthOfStay} onChange={set("lengthOfStay")} placeholder="e.g. 2 weeks" />
                                        </Field>
                                    </div>
                                    <Field label="Treatment or Medical Support Needed" htmlFor="hc-treatment" required>
                                        <textarea id="hc-treatment" data-testid="hc-treatment" required rows={3} className={inputCls} value={form.treatmentNeeded} onChange={set("treatmentNeeded")} />
                                    </Field>
                                    <Field label="Existing Diagnosis, Reports or Treatment History" htmlFor="hc-history" hint="Summarise what you can — you may also attach documents below.">
                                        <textarea id="hc-history" data-testid="hc-history" rows={3} className={inputCls} value={form.medicalHistory} onChange={set("medicalHistory")} />
                                    </Field>
                                    <Field label="Documents or Images" htmlFor="hc-file" hint="Optional. PDF, PNG or JPG, max 3 MB. Transmitted securely with your enquiry only.">
                                        <FileField id="hc-file" file={file} onChange={setFile} />
                                    </Field>
                                    <label htmlFor="hc-consent" className="flex items-start gap-3 text-sm text-slate-300">
                                        <input id="hc-consent" data-testid="hc-consent" required type="checkbox" checked={form.consentToShare} onChange={set("consentToShare")} className="mt-1 h-4 w-4 accent-[#C5A059]" />
                                        I consent to Viet Medica Ltd sharing this information with relevant healthcare providers for the purpose of coordinating my enquiry.
                                    </label>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="hc-submit"
                                        className="w-full bg-gold px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-navy-deep transition-colors hover:bg-gold-bright disabled:opacity-50"
                                    >
                                        {submitting ? "Sending…" : "Send Healthcare Enquiry"}
                                    </button>
                                </form>
                            </FadeUp>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
